import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { doc, updateDoc, writeBatch, collection } from "firebase/firestore";
import { db } from "../../../api/firebase";
import { addNotification } from "../../../store/notificationSlice";

// --- ACTION #1: Save Tenant Config (Root Document) ---
export const saveTenantConfig = createAsyncThunk(
  "crudTenant/saveTenantConfig",
  async ({ tenantId, config }, { dispatch, rejectWithValue }) => {
    try {
      const docRef = doc(db, "tenants", tenantId);
      await updateDoc(docRef, config);

      dispatch(
        addNotification({
          type: "success",
          message: `La configuración se guardó correctamente.`,
        })
      );
      return config;
    } catch (e) {
      return rejectWithValue(`Error config: ${e.message}`);
    }
  }
);

// --- ACTION #2: Save Tenant Catalog (Subcollections) ---
export const saveTenantCatalog = createAsyncThunk(
  "crudTenant/saveTenantCatalog",
  async ({ tenantId, catalogMap }, { dispatch, rejectWithValue }) => {
    try {
      const batch = writeBatch(db);

      // catalogMap structure: { "services": [item1, item2], "products": [...] }
      const sections = Object.entries(catalogMap);

      for (const [slug, items] of sections) {
        // 1. Reference/Create Category
        const categoryRef = doc(db, "tenants", tenantId, "catalog", slug);
        batch.set(
          categoryRef,
          { slug: slug, lastUpdated: new Date() },
          { merge: true }
        );

        // 2. Reference Items Subcollection
        const itemsCollectionRef = collection(
          db,
          "tenants",
          tenantId,
          "catalog",
          slug,
          "items"
        );

        if (Array.isArray(items)) {
          items.forEach((item) => {
            if (item.id) {
              const itemRef = doc(itemsCollectionRef, item.id);
              batch.set(itemRef, item, { merge: true });
            } else {
              // Generate ID if new
              const newItemRef = doc(itemsCollectionRef);
              batch.set(newItemRef, { ...item, id: newItemRef.id });
            }
          });
        }
      }

      await batch.commit();

      dispatch(
        addNotification({
          type: "success",
          message: `El catálogo se actualizó correctamente.`,
        })
      );
      return catalogMap;
    } catch (e) {
      console.error("Save Catalog Error:", e);
      return rejectWithValue(`Error catálogo: ${e.message}`);
    }
  }
);

const initialState = {
  loading: false,
  error: null,
  message: null,
};

const tenantEditorSlice = createSlice({
  name: "crudTenant",
  initialState,
  reducers: {
    clearStatus(state) {
      state.error = null;
      state.message = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Config
      .addCase(saveTenantConfig.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveTenantConfig.fulfilled, (state) => {
        state.loading = false;
        state.message = "Configuración OK";
      })
      .addCase(saveTenantConfig.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Catalog
      .addCase(saveTenantCatalog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(saveTenantCatalog.fulfilled, (state) => {
        state.loading = false;
        state.message = "Catálogo OK";
      })
      .addCase(saveTenantCatalog.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { clearStatus } = tenantEditorSlice.actions;
export default tenantEditorSlice.reducer;
